import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import { Flame } from "lucide-react";

/**
 * Goal Completion Modal - 50 Tasks
 * 
 * Grounded, meaningful, earned — not overhyped.
 * Completion with emotional weight.
 */

// Soft cinematic tone - warm, low, short
const playCompletionSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playTone = (time, freq, volume, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(freq, time);
      oscillator.frequency.exponentialRampToValueAtTime(freq * 0.7, time + duration);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, time);
      gainNode.gain.linearRampToValueAtTime(volume, time + 0.03);
      gainNode.gain.exponentialRampToValueAtTime(0.001, time + duration);
      
      oscillator.start(time);
      oscillator.stop(time + duration + 0.1);
    };
    
    const now = audioContext.currentTime;
    // Warm, low cinematic tone
    playTone(now, 110, 0.08, 0.4);
    playTone(now + 0.05, 165, 0.06, 0.35);
    
  } catch (e) {
    // Audio not supported
  }
};

// Two-stage heartbeat - deeper than 25 milestone
const triggerArrivalHaptic = () => {
  if ('vibrate' in navigator) {
    // First pulse - deep, grounded
    navigator.vibrate(80);
    // Second pulse - slightly softer, settling
    setTimeout(() => navigator.vibrate(60), 200);
  }
};

// Generate elegant particles (more than 25, but still refined)
const generateElegantParticles = () => {
  const count = 16;
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    angle: (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4,
    distance: 80 + Math.random() * 50,
    size: 3.5 + Math.random() * 3,
    delay: 0.35 + (i * 0.05),
    duration: 1.4 + Math.random() * 0.5,
    color: [
      'rgba(249, 115, 22, 0.55)',   // Orange
      'rgba(251, 146, 60, 0.5)',    // Light orange
      'rgba(239, 68, 68, 0.45)',    // Red
      'rgba(251, 191, 36, 0.5)',    // Amber
      'rgba(254, 215, 170, 0.45)',  // Peach
      'rgba(253, 186, 116, 0.5)',   // Soft orange
    ][Math.floor(Math.random() * 6)],
  }));
};

export const GoalCompletionModal = ({ isOpen, onClose }) => {
  const [particles, setParticles] = useState([]);
  const hasPlayedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !hasPlayedRef.current) {
      hasPlayedRef.current = true;
      setParticles(generateElegantParticles());
      
      // Delayed sound and haptic for cinematic arrival
      setTimeout(() => {
        playCompletionSound();
        triggerArrivalHaptic();
      }, 300);
    }
    
    if (!isOpen) {
      hasPlayedRef.current = false;
    }
  }, [isOpen]);

  const handleDismiss = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(40);
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop - slightly more emphasis */}
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleDismiss}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative bg-[#FFFAF5] rounded-3xl p-9 max-w-sm mx-4 pointer-events-auto"
              style={{
                boxShadow: '0 25px 70px rgba(0,0,0,0.18), 0 10px 30px rgba(249,115,22,0.08)',
              }}
              initial={{ opacity: 0, y: 24, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Elegant particles */}
              <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
                {particles.map((particle) => (
                  <motion.div
                    key={particle.id}
                    className="absolute rounded-full"
                    style={{
                      width: particle.size,
                      height: particle.size,
                      left: '50%',
                      top: '30%',
                      backgroundColor: particle.color,
                    }}
                    initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
                    animate={{
                      opacity: [0, 0.75, 0.5, 0],
                      scale: [0, 1.1, 0.9, 0.3],
                      x: Math.cos(particle.angle) * particle.distance,
                      y: Math.sin(particle.angle) * particle.distance,
                    }}
                    transition={{
                      duration: particle.duration,
                      delay: particle.delay,
                      ease: [0.1, 0.15, 0.25, 1],
                    }}
                  />
                ))}
              </div>

              {/* Icon with warm glow bloom and ripples */}
              <div className="relative flex justify-center mb-7">
                {/* Warm glow bloom - expands and settles */}
                <motion.div
                  className="absolute w-28 h-28 rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(249, 115, 22, 0.2) 0%, rgba(239, 68, 68, 0.1) 40%, transparent 70%)',
                  }}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ 
                    scale: [0.5, 1.5, 1.3],
                    opacity: [0, 0.8, 0.5]
                  }}
                  transition={{ 
                    duration: 1.2, 
                    ease: [0.25, 0.1, 0.3, 1],
                  }}
                />
                
                {/* Outer ripple - wider than 25 milestone */}
                <motion.div
                  className="absolute w-24 h-24 rounded-full"
                  style={{
                    border: '1.5px solid rgba(249, 115, 22, 0.2)',
                  }}
                  animate={{
                    scale: [1, 2.5, 3],
                    opacity: [0.5, 0.25, 0],
                  }}
                  transition={{
                    duration: 2.2,
                    repeat: 1,
                    ease: [0.2, 0.1, 0.3, 1],
                  }}
                />
                
                {/* Inner ripple */}
                <motion.div
                  className="absolute w-24 h-24 rounded-full"
                  style={{
                    border: '1px solid rgba(249, 115, 22, 0.15)',
                  }}
                  animate={{
                    scale: [1, 2, 2.5],
                    opacity: [0.4, 0.2, 0],
                  }}
                  transition={{
                    duration: 1.8,
                    delay: 0.4,
                    repeat: 1,
                    ease: [0.2, 0.1, 0.3, 1],
                  }}
                />
                
                {/* Flame icon - brighter, with glow */}
                <motion.div
                  className="relative z-10 w-18 h-18 rounded-full flex items-center justify-center"
                  style={{
                    width: 72,
                    height: 72,
                    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 40%, #DC2626 100%)',
                    boxShadow: '0 6px 25px rgba(249, 115, 22, 0.5), 0 0 40px rgba(249, 115, 22, 0.2)',
                  }}
                  initial={{ scale: 0.85, opacity: 0.8 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <Flame 
                    className="text-white" 
                    size={32} 
                    strokeWidth={2.5} 
                    fill="rgba(255,255,255,0.35)" 
                  />
                </motion.div>
              </div>

              {/* Text content */}
              <div className="text-center relative z-10">
                <motion.h2
                  className="text-2xl font-bold text-[#1A1A1C] mb-1.5"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.2 }}
                >
                  You Did It
                </motion.h2>
                
                <motion.p
                  className="text-base text-[#6B6B6B] mb-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.25 }}
                >
                  50 tasks completed
                </motion.p>
                
                <motion.p
                  className="text-sm text-[#8B8B8B] mb-2 leading-relaxed"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.3 }}
                >
                  That didn't happen by accident.<br />
                  You stayed with it.
                </motion.p>
                
                <motion.p
                  className="text-xs text-[#AAAAAA] mb-7"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.75 }}
                  transition={{ duration: 0.4, delay: 0.4 }}
                >
                  Take a second. Then keep going when you're ready.
                </motion.p>

                {/* Primary button */}
                <motion.button
                  className="relative px-8 py-3.5 rounded-full text-white font-semibold text-base overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #F97316 0%, #EA580C 50%, #DC2626 100%)',
                  }}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.35 }}
                  whileHover={{ 
                    scale: 1.02,
                    boxShadow: '0 8px 30px rgba(249, 115, 22, 0.5)',
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDismiss}
                  data-testid="goal-completion-continue-btn"
                >
                  <span className="relative z-10">Start the next 50 🔥</span>
                  
                  {/* Subtle inner glow */}
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{
                      background: 'radial-gradient(circle at center, rgba(255,255,255,0.25) 0%, transparent 60%)',
                    }}
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.25 }}
                  />
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default GoalCompletionModal;
